import type { RouteRecordRaw } from 'vue-router'
import type { CustomRouteMeta } from '@/router'

// Route configuration interface
export interface RouteConfig {
  path: string
  name: string
  component: () => Promise<any>
  meta: {
    title?: string
    group?: string
    icon?: string
    i18nKey?: string
    requiresAuth?: boolean
    functionType?: 'document' | 'flashcard' | 'practice' | 'exercise' | 'exam'
    /** Only allow ADMIN to access this route */
    requiresAdmin?: boolean
    breadcrumb?: {
      parent?: string[]
      title?: string
      icon?: string
      i18nKey?: string
      params?: string[]
    }
  }
  children?: RouteConfig[]
}

// Central route configuration - SINGLE SOURCE OF TRUTH
export const ROUTE_CONFIGS: RouteConfig[] = [
  // HOME ROUTE (trang chủ chung)
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/agent-management/index.vue'),
    meta: {
      group: 'home',
      icon: 'ri:home-9-line',
      i18nKey: 'menu.home',
      breadcrumb: {
        title: 'Trang chủ',
        icon: 'ri:home-9-line',
        i18nKey: 'menu.home',
      },
    },
  },

  // AGENT MANAGEMENT GROUP
  {
    path: '/agent-management',
    name: 'dashboard',
    component: () => import('@/views/agent-management/index.vue'),
    meta: {
      group: 'agent-management',
      icon: 'ri:folders-line',
      i18nKey: 'menu.dashboard',
      breadcrumb: {
        parent: ['home'],
        title: 'Dashboard',
        icon: 'ri:folders-line',
        i18nKey: 'menu.dashboard',
      },
    },
  },
  {
    path: '/folder/:folderId',
    name: 'folder',
    component: () => import('@/views/folder/index.vue'),
    meta: {
      group: 'agent-management',
      icon: 'ri:folder-line',
      i18nKey: 'menu.folder',
      breadcrumb: {
        parent: ['home'],
        title: 'Folder',
        icon: 'ri:folder-line',
        i18nKey: 'menu.folder',
        params: ['folderId'],
      },
    },
  },
  {
    path: '/agent/:agentId',
    name: 'chat',
    component: () => import('@/views/chat/index.vue'),
    meta: {
      group: 'agent-management',
      icon: 'ri:chat-3-line',
      i18nKey: 'menu.chat',
      breadcrumb: {
        parent: ['home'],
        title: 'Chat',
        icon: 'ri:chat-3-line',
        i18nKey: 'menu.chat',
        params: ['agentId'],
      },
    },
  },
  {
    path: '/add-agent',
    name: 'add-agent',
    component: () => import('@/views/agent-management/add-agent/index.vue'),
    meta: {
      group: 'agent-management',
      icon: 'ri:add-circle-line',
      i18nKey: 'menu.addAgent',
      breadcrumb: {
        parent: ['home'],
        title: 'Add Agent',
        icon: 'ri:add-circle-line',
        i18nKey: 'menu.addAgent',
      },
    },
  },
  {
    path: '/agent/configure/:agentId',
    name: 'agent-configure',
    component: () => import('@/views/agent-management/update-agent/index.vue'),
    meta: {
      group: 'agent-management',
      icon: 'ri:settings-3-line',
      i18nKey: 'menu.configureAgent',
      breadcrumb: {
        parent: ['home'],
        title: 'Configure Agent',
        icon: 'ri:settings-3-line',
        i18nKey: 'menu.configureAgent',
        params: ['agentId'],
      },
    },
  },
  {
    path: '/monitor/:agentId',
    name: 'monitor',
    component: () => import('@/views/folder/Monitoring.vue'),
    meta: {
      group: 'agent-management',
      icon: 'ri:eye-line',
      i18nKey: 'menu.monitor',
      breadcrumb: {
        parent: ['home'],
        title: 'Monitor',
        icon: 'ri:eye-line',
        i18nKey: 'menu.monitor',
        params: ['agentId'],
      },
    },
  },

  // AGENT STORE GROUP
  {
    path: '/agent-store',
    name: 'agent-store',
    component: () => import('@/views/agent-store/index.vue'),
    meta: {
      group: 'agent-store',
      icon: 'ri:store-2-line',
      i18nKey: 'menu.agentStore',
      breadcrumb: {
        parent: ['home'],
        title: 'Agent Store',
        icon: 'ri:store-2-line',
        i18nKey: 'menu.agentStore',
      },
    },
  },
  {
    path: '/agent-store/view-agent/:agentId',
    name: 'ViewAgent',
    component: () => import('@/views/agent-store/components/DetailAgent.vue'),
    meta: {
      group: 'agent-store',
      icon: 'ri:robot-line',
      i18nKey: 'menu.viewAgent',
      breadcrumb: {
        parent: ['home', 'agent-store'],
        title: 'View Agent',
        icon: 'ri:robot-line',
        i18nKey: 'menu.viewAgent',
        params: ['agentId'],
      },
    },
  },
  {
    path: '/agent-store/view-educational-resource/:agentId',
    name: 'ViewEducationalResource',
    component: () => import('@/views/agent-store/components/DetailEducationalResource.vue'),
    meta: {
      group: 'agent-store',
      icon: 'ri:book-open-line',
      i18nKey: 'menu.viewEducationalResource',
      breadcrumb: {
        parent: ['home', 'agent-store'],
        title: 'View Educational Resource',
        icon: 'ri:book-open-line',
        i18nKey: 'menu.viewEducationalResource',
        params: ['agentId'],
      },
    },
  },
  {
    path: '/agent-store/upload-agent',
    name: 'UploadAgent',
    component: () => import('@/views/agent-store/components/upload-agent/index.vue'),
    meta: {
      group: 'agent-store',
      icon: 'ri:upload-cloud-line',
      i18nKey: 'menu.uploadAgent',
      breadcrumb: {
        parent: ['home', 'agent-store'],
        title: 'Upload Agent',
        icon: 'ri:upload-cloud-line',
        i18nKey: 'menu.uploadAgent',
      },
    },
  },

  // IMAGE GENERATION GROUP
  {
    path: '/image-generation',
    name: 'image-generation',
    component: () => import('@/views/image-generation/index.vue'),
    meta: {
      group: 'image-generation',
      icon: 'ri:multi-image-line',
      i18nKey: 'menu.imageGeneration',
      breadcrumb: {
        parent: ['home'],
        title: 'Image Generation',
        icon: 'ri:multi-image-line',
        i18nKey: 'menu.imageGeneration',
      },
    },
  },
  // KNOWLEDGE GROUP
  {
    path: '/knowledge',
    name: 'knowledge',
    component: () => import('@/views/knowledges/knowledge.vue'),
    meta: {
      group: 'knowledge',
      icon: 'ri:book-3-line',
      i18nKey: 'menu.knowledges',
      breadcrumb: {
        parent: ['home'],
        title: 'Knowledge Base',
        icon: 'ri:book-3-line',
        i18nKey: 'menu.knowledges',
      },
    },
  },
  {
    path: '/knowledge/create',
    name: 'knowledge.create',
    component: () => import('@/views/knowledges/Create.vue'),
    meta: {
      group: 'knowledge',
      icon: 'ri:add-line',
      i18nKey: 'menu.createKnowledge',
      breadcrumb: {
        parent: ['home', 'knowledge'],
        title: 'Create Knowledge',
        icon: 'ri:add-line',
        i18nKey: 'menu.createKnowledge',
      },
    },
  },
  {
    path: '/knowledge/:knowledgeId',
    name: 'knowledge.detail',
    component: () => import('@/views/knowledges/documents/index.vue'),
    meta: {
      group: 'knowledge',
      icon: 'ri:book-open-line',
      i18nKey: 'menu.knowledgeDetail',
      breadcrumb: {
        parent: ['home', 'knowledge'],
        title: 'Knowledge Detail',
        icon: 'ri:book-open-line',
        i18nKey: 'menu.knowledgeDetail',
        params: ['knowledgeId'],
      },
    },
  },
  {
    path: '/knowledge/:knowledgeId/document/create',
    name: 'documents.create',
    component: () => import('@/views/knowledges/Create.vue'),
    meta: {
      group: 'knowledge',
      icon: 'ri:file-add-line',
      i18nKey: 'menu.createDocument',
      breadcrumb: {
        parent: ['home', 'knowledge', 'knowledge.detail'],
        title: 'Create Document',
        icon: 'ri:file-add-line',
        i18nKey: 'menu.createDocument',
        params: ['knowledgeId'],
      },
    },
  },
  {
    path: '/knowledge/:knowledgeId/document/:documentId',
    name: 'documents.detail',
    component: () => import('@/views/knowledges/documents/Detail.vue'),
    meta: {
      group: 'knowledge',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.documentDetail',
      breadcrumb: {
        parent: ['home', 'knowledge', 'knowledge.detail'],
        title: 'Document Detail',
        icon: 'ri:file-text-line',
        i18nKey: 'menu.documentDetail',
        params: ['knowledgeId', 'documentId'],
      },
    },
  },
  {
    path: '/knowledge/:knowledgeId/document/:documentId/settings',
    name: 'documents.settings',
    component: () => import('@/views/knowledges/documents/Settings.vue'),
    meta: {
      group: 'knowledge',
      icon: 'ri:settings-4-line',
      i18nKey: 'menu.documentSettings',
      breadcrumb: {
        parent: ['home', 'knowledge', 'knowledge.detail', 'documents.detail'],
        title: 'Document Settings',
        icon: 'ri:settings-4-line',
        i18nKey: 'menu.documentSettings',
        params: ['knowledgeId', 'documentId'],
      },
    },
  },
  {
    path: '/knowledge/text-extraction',
    name: 'knowledge.extraction',
    component: () => import('@/views/knowledges/components/TextExtraction/index.vue'),
    meta: {
      group: 'knowledge',
      icon: 'ri:scan-line',
      i18nKey: 'menu.textExtraction',
      breadcrumb: {
        parent: ['home', 'knowledge'],
        title: 'Text Extraction',
        icon: 'ri:scan-line',
        i18nKey: 'menu.textExtraction',
      },
    },
  },

  // TASKS GROUP
  {
    path: '/tasks',
    name: 'tasks',
    component: () => import('@/views/tasks/index.vue'),
    meta: {
      group: 'tasks',
      icon: 'ri:briefcase-line',
      i18nKey: 'menu.tasks',
      breadcrumb: {
        parent: ['home'],
        title: 'Tasks',
        icon: 'ri:briefcase-line',
        i18nKey: 'menu.tasks',
      },
    },
  },
  {
    path: '/tasks/assign-agent/:taskId',
    name: 'task-assign-agent',
    component: () => import('@/views/tasks/AssignAgent.vue'),
    meta: {
      group: 'tasks',
      icon: 'ri:user-add-line',
      i18nKey: 'menu.assignAgent',
      breadcrumb: {
        parent: ['home', 'tasks'],
        title: 'Assign Agent',
        icon: 'ri:user-add-line',
        i18nKey: 'menu.assignAgent',
        params: ['taskId'],
      },
    },
  },
  // SCHOOL GROUP
  {
    path: '/school',
    name: 'school',
    component: () => import('@/views/school-management/Dashboard.vue'),
    meta: {
      group: 'school',
      icon: 'ri:school-line',
      i18nKey: 'menu.school',
      breadcrumb: {
        parent: ['home'],
        title: 'School',
        icon: 'ri:school-line',
        i18nKey: 'menu.school',
      },
    },
  },

  // CLASSROOM GROUP
  {
    path: '/classroom-v1',
    name: 'classroom',
    component: () => import('@/views/classroom/index.vue'),
    meta: {
      group: 'classroom',
      icon: 'ri:team-line',
      i18nKey: 'menu.classroom',
      breadcrumb: {
        parent: ['home'],
        title: 'Classroom',
        icon: 'ri:team-line',
        i18nKey: 'menu.classroom',
      },
    },
  },
  {
    path: '/classroom/:classroomId',
    name: 'classroom.detail',
    component: () => import('@/views/classroom/Detail.vue'),
    meta: {
      group: 'classroomDetail',
      icon: 'ri:menu-search-line',
      i18nKey: 'menu.classroomDetail',
      breadcrumb: {
        parent: ['home', 'classroom'],
        title: 'Classroom Detail',
        icon: 'ri:menu-search-line',
        i18nKey: 'menu.classroomDetail',
      },
    },
  },
  {
    path: '/classroom/:classroomId/result',
    name: 'classroom.result',
    component: () => import('@/views/classroom/components/Detail/ResultTab.vue'),
    meta: {
      group: 'classroomDetail',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.classroomResult',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Result',
        icon: 'mdi:puzzle-outline',
        i18nKey: 'menu.classroomResult',
      },
    },
  },
  {
    path: '/classroom/:classroomId/result/student/:studentId',
    name: 'classroom.result.student',
    component: () => import('@/views/classroom/components/Result/index.vue'),
    meta: {
      group: 'classroomSubject',
      icon: 'ri:user-line',
      i18nKey: 'menu.classroomStudentDetail',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Student Detail',
        icon: 'ri:user-line',
        i18nKey: 'menu.classroomStudentDetail',
        params: ['classroomId', 'studentId'],
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId',
    name: 'classroom.subject',
    component: () => import('@/views/classroom/components/Subject/index.vue'),
    meta: {
      group: 'classroomSubject',
      icon: 'mdi:puzzle-outline',
      i18nKey: 'menu.classroomDetailSubject',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Subject Detail',
        icon: 'mdi:puzzle-outline',
        i18nKey: 'menu.classroomDetailSubject',
      },
    },
  },

  {
    path: '/classroom/:classroomId/subject/:subjectId/assign-agent',
    name: 'classroom.assign-agent',
    component: () => import('@/views/classroom/AssignAgent.vue'),
    meta: {
      group: 'classroomAssignAgent',
      icon: 'ri:user-add-line',
      i18nKey: 'menu.assignAgent',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Assign Agent',
        icon: 'ri:user-add-line',
        i18nKey: 'menu.assignAgent',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/lesson/:lessonId/create-doc',
    name: 'classroom.create.uploadDocument',
    component: () => import('@/views/classroom/components/Content/UploadDocument.vue'),
    meta: {
      group: 'classroomUploadDocument',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.classroomUploadDocument',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Upload Document',
        icon: 'ri:file-text-line',
        i18nKey: 'menu.classroomUploadDocument',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/lesson/:lessonId/create-doc-tool',
    name: 'classroom.create.uploadDocumentTool',
    component: () => import('@/views/classroom/components/Content/SelectFromTool.vue'),
    meta: {
      group: 'classroomUploadDocument',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.classroomUploadDocument',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Upload Document',
        icon: 'ri:file-text-line',
        i18nKey: 'menu.classroomUploadDocument',
      },
    },
  },
  // {
  //   path: '/classroom/:classroomId/subject/:subjectId/test-builder',
  //   name: 'classroom.create.test-builder',
  //   component: () => import('@/views/classroom/components/Content/Question.vue'),
  //   meta: {
  //     group: 'classroomSubject',
  //     icon: 'ri:file-text-line',
  //     i18nKey: 'menu.classroomTestBuilder',
  //     breadcrumb: {
  //       parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
  //       title: 'Test Builder',
  //       icon: 'ri:file-text-line',
  //       i18nKey: 'menu.classroomTestBuilder',
  //     },
  //   },
  // },
  {
    path: '/classroom/:classroomId/subject/:subjectId/lesson/:lessonId/create-practice',
    name: 'classroom.create.createPractice',
    component: () => import('@/views/classroom/components/Content/CreatePractice.vue'),
    meta: {
      group: 'classroomCreatePractice',
      icon: 'ri:list-check-3',
      i18nKey: 'menu.classroomCreatePractice',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Create Practice',
        icon: 'ri:list-check-3',
        i18nKey: 'menu.classroomCreatePractice',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/lesson/:lessonId/create-flashcard',
    name: 'classroom.create.createFlashcard',
    component: () => import('@/views/classroom/components/Content/CreateFlashcard.vue'),
    meta: {
      group: 'classroomCreateFlashcard',
      icon: 'mdi:cards-outline',
      i18nKey: 'menu.classroomCreateFlashcard',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Create Flashcard',
        icon: 'mdi:cards-outline',
        i18nKey: 'menu.classroomCreateFlashcard',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/lesson/:lessonId/create-exercise',
    name: 'classroom.create.createExercise',
    component: () => import('@/views/classroom/components/Content/CreateExercise.vue'),
    meta: {
      group: 'classroomCreateExercise',
      icon: 'ri:edit-2-line',
      i18nKey: 'menu.classroomCreateExercise',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Create Exercise',
        icon: 'ri:edit-2-line',
        i18nKey: 'menu.classroomCreateExercise',
      },
    },
  },
  {
    path: '/classroom/:classroomId/create-exercise',
    name: 'classroom.createExercise',
    component: () => import('@/views/classroom/components/Exercise/Index.vue'),
    meta: {
      group: 'classroomCreateExercise',
      icon: 'ri:edit-2-line',
      i18nKey: 'menu.classroomCreateExercise',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Create Exercise',
        icon: 'ri:edit-2-line',
        i18nKey: 'menu.classroomCreateExercise',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/practice-exercise/:exerciseId',
    name: 'classroom.practiceExercise',
    component: () => import('@/views/classroom/components/Subject/PracticeExercise.vue'),
    meta: {
      group: 'classroomPracticeExercise',
      icon: 'ri:list-check-3',
      i18nKey: 'menu.classroomPracticeExercise',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Practice Exercise',
        icon: 'ri:list-check-3',
        i18nKey: 'menu.classroomPracticeExercise',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/exercise/:exerciseId',
    name: 'classroom.exerciseDetail',
    component: () => import('@/views/classroom/components/Subject/ExcerciseDetail.vue'),
    meta: {
      group: 'classroomExerciseDetail',
      icon: 'ri:edit-2-line',
      i18nKey: 'menu.classroomExerciseDetail',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Exercise Detail',
        icon: 'ri:edit-2-line',
        i18nKey: 'menu.classroomExerciseDetail',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/exercise-progress/:exerciseId',
    name: 'classroom.exerciseProgressDetail',
    component: () => import('@/views/classroom/components/Subject/ExerciseProgressDetail.vue'),
    meta: {
      group: 'classroomExerciseProgressDetail',
      icon: 'ri:bar-chart-line',
      i18nKey: 'menu.classroomExerciseProgressDetail',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Exercise Progress Detail',
        icon: 'ri:bar-chart-line',
        i18nKey: 'menu.classroomExerciseProgressDetail',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/exercise/:exerciseId/student/:studentId/result',
    name: 'classroom.studentExerciseResult',
    component: () => import('@/views/classroom/components/Subject/components/screens/StudentExerciseResult.vue'),
    meta: {
      group: 'classroomStudentExerciseResult',
      icon: 'ri:edit-line',
      i18nKey: 'menu.classroomStudentExerciseResult',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject', 'classroom.exerciseProgressDetail'],
        title: 'Student Exercise Result',
        icon: 'ri:edit-line',
        i18nKey: 'menu.classroomStudentExerciseResult',
      },
    },
  },

  // Exam Progress Routes
  {
    path: '/classroom/:classroomId/subject/:subjectId/exam-progress/:examId',
    name: 'classroom.examProgressDetail',
    component: () => import('@/views/classroom/components/Subject/ExamProgressDetail.vue'),
    meta: {
      group: 'classroomExamProgressDetail',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.classroomExamProgressDetail',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Exam Progress Detail',
        icon: 'ri:file-text-line',
        i18nKey: 'menu.classroomExamProgressDetail',
        params: ['classroomId', 'subjectId', 'examId'],
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/exam/:examId/student/:studentId/result',
    name: 'classroom.studentExamResult',
    component: () => import('@/views/classroom/components/Subject/components/screens/StudentExamResult.vue'),
    meta: {
      group: 'classroomStudentExamResult',
      icon: 'ri:edit-line',
      i18nKey: 'menu.classroomStudentExamResult',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject', 'classroom.examProgressDetail'],
        title: 'Student Exam Result',
        icon: 'ri:edit-line',
        i18nKey: 'menu.classroomStudentExamResult',
        params: ['classroomId', 'subjectId', 'examId', 'studentId'],
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/exercise/:exerciseId',
    name: 'classroom.exerciseDetail',
    component: () => import('@/views/classroom/components/Subject/ExcerciseDetail.vue'),
    meta: {
      group: 'classroomExerciseDetail',
      icon: 'ri:edit-2-line',
      i18nKey: 'menu.classroomExerciseDetail',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Exercise Detail',
        icon: 'ri:edit-2-line',
        i18nKey: 'menu.classroomExerciseDetail',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/document/:documentId',
    name: 'classroom.detail.document',
    component: () => import('@/views/classroom/components/Subject/DocumentDetail.vue'),
    meta: {
      group: 'classroomUploadDocument',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.classroomUploadDocument',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Upload Document',
        icon: 'ri:file-text-line',
        i18nKey: 'menu.classroomUploadDocument',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/upload-teaching-plan',
    name: 'classroom.uploadTeachingPlan',
    component: () => import('@/views/classroom/components/Subject/components/uploads/UploadTeachingPlan.vue'),
    meta: {
      group: 'classroomUploadTeachingPlan',
      icon: 'ri:upload-cloud-line',
      i18nKey: 'menu.classroomUploadTeachingPlan',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Upload Teaching Plan',
        icon: 'ri:upload-cloud-line',
        i18nKey: 'menu.classroomUploadTeachingPlan',
        params: ['classroomId', 'subjectId'],
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/upload-lesson-plan',
    name: 'classroom.uploadLessonPlan',
    component: () => import('@/views/classroom/components/Subject/components/uploads/UploadLessonPlan.vue'),
    meta: {
      group: 'classroomUploadLessonPlan',
      icon: 'ri:upload-cloud-line',
      i18nKey: 'menu.classroomUploadLessonPlan',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Upload Lesson Plan',
        icon: 'ri:upload-cloud-line',
        i18nKey: 'menu.classroomUploadLessonPlan',
        params: ['classroomId', 'subjectId'],
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/upload-lesson-plan-tool',
    name: 'classroom.uploadLessonPlanTool',
    component: () => import('@/views/classroom/components/Subject/components/uploads/UploadLessonPlanTool.vue'),
    meta: {
      group: 'classroomUploadLessonPlanTool',
      icon: 'ri:upload-cloud-line',
      i18nKey: 'menu.classroomUploadLessonPlanTool',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Upload Lesson Plan Tool',
        icon: 'ri:upload-cloud-line',
        i18nKey: 'menu.classroomUploadLessonPlanTool',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/upload-teaching-plan-tool',
    name: 'classroom.uploadTeachingPlanTool',
    component: () => import('@/views/classroom/components/Subject/components/uploads/UploadTeachingPlanTool.vue'),
    meta: {
      group: 'classroomUploadTeachingPlanTool',
      icon: 'ri:upload-cloud-line',
      i18nKey: 'menu.classroomUploadTeachingPlanTool',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Upload Teaching Plan Tool',
        icon: 'ri:upload-cloud-line',
        i18nKey: 'menu.classroomUploadTeachingPlanTool',
      },
    },
  },

  {
    path: '/classroom/:classroomId/subject/:subjectId/exam/:examId/students',
    name: 'classroom.exam.students',
    component: () => import('@/views/classroom/components/Subject/ExamStudentList.vue'),
    meta: {
      group: 'classroomExam',
      icon: 'ri:user-line',
      i18nKey: 'menu.classroomExamStudents',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Exam Students',
        icon: 'ri:user-line',
        i18nKey: 'menu.classroomExamStudents',
        params: ['classroomId', 'subjectId', 'examId'],
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/exam/:examId/grade/:submitId',
    name: 'classroom.exam.grade',
    component: () => import('@/views/classroom/components/Subject/components/screens/ExamGradeView.vue'),
    meta: {
      group: 'classroomExamGrade',
      icon: 'ri:edit-line',
      i18nKey: 'menu.classroomExamGrade',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject', 'classroom.exam.students'],
        title: 'Grade Exam',
        icon: 'ri:edit-line',
        i18nKey: 'menu.classroomExamGrade',
        params: ['classroomId', 'subjectId', 'examId', 'submitId'],
      },
    },
  },

  {
    path: '/classroom/exam-paper',
    name: 'classroom.exam-paper',
    component: () => import('@/views/classroom/ExamPaper.vue'),
    meta: {
      group: 'classroom',
      icon: 'ri:file-edit-line',
      i18nKey: 'menu.classroomExamPaper',
      breadcrumb: {
        parent: ['home', 'classroom'],
        title: 'ExamPaper',
        icon: 'ri:file-edit-line',
        i18nKey: 'menu.classroomExamPaper',
      },
    },
  },
  {
    path: '/classroom/:classroomId/exam/:examId',
    name: 'classroom.examDetail',
    component: () => import('@/views/classroom/components/Detail/ExamDetail.vue'),
    meta: {
      group: 'classroomExamDetail',
      icon: 'ri:edit-2-line',
      i18nKey: 'menu.classroomExamDetail',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Exam Detail',
        icon: 'ri:edit-2-line',
        i18nKey: 'menu.classroomExamDetail',
      },
    },
  },
  {
    path: '/classroom/:classroomId/exam-detail/:examId',
    name: 'classroom.examDetailScreen',
    component: () => import('@/views/classroom/components/Detail/ExamDetailScreen.vue'),
    meta: {
      group: 'classroomExamDetail',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.classroomExamDetail',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Chi tiết bài kiểm tra',
        icon: 'ri:file-text-line',
        i18nKey: 'menu.classroomExamDetail',
      },
    },
  },
  {
    path: '/classroom/:classroomId/exam-waiting/:examId',
    name: 'classroom.examWaitingScreen',
    component: () => import('@/views/classroom/components/Detail/ExamWaitingScreen.vue'),
    meta: {
      group: 'classroomExamWaiting',
      icon: 'ri:time-line',
      i18nKey: 'menu.classroomExamWaiting',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Chờ bài kiểm tra',
        icon: 'ri:time-line',
        i18nKey: 'menu.classroomExamWaiting',
      },
    },
  },
  {
    path: '/classroom/:classroomId/exam-ended/:examId',
    name: 'classroom.examEndedScreen',
    component: () => import('@/views/classroom/components/Detail/ExamEndedScreen.vue'),
    meta: {
      group: 'classroomExamEnded',
      icon: 'ri:time-line',
      i18nKey: 'menu.classroomExamEnded',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Bài kiểm tra đã kết thúc',
        icon: 'ri:time-line',
        i18nKey: 'menu.classroomExamEnded',
      },
    },
  },
  {
    path: '/classroom/:classroomId/exam-completion',
    name: 'classroom.examCompletion',
    component: () => import('@/views/classroom/components/Detail/CompletionScreen.vue'),
    meta: {
      group: 'classroomExamCompletion',
      icon: 'ri:trophy-line',
      i18nKey: 'menu.classroomExamCompletion',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail'],
        title: 'Bài kiểm tra đã được nộp',
        icon: 'ri:trophy-line',
        i18nKey: 'menu.classroomExamCompletion',
      },
    },
  },
  {
    path: '/classroom/question-bank/:questionBankId',
    name: 'classroom.question-bank',
    component: () => import('@/views/classroom/components/ExamPaper/QuestionBank.vue'),
    meta: {
      group: 'classroom',
      icon: 'ri:edit-2-line',
      i18nKey: 'menu.classroomQuestionBank',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.exam-paper'],
        title: 'Question Bank',
        icon: 'ri:edit-2-line',
        i18nKey: 'menu.classroomQuestionBank',
      },
    },
  },
  {
    path: '/classroom/exam-paper/:examPaperId/add-question-ai',
    name: 'classroom.add-question-ai',
    component: () => import('@/views/classroom/components/ExamPaper/AutoAIGeneration.vue'),
    meta: {
      group: 'classroom',
      icon: 'ri:list-check-3',
      i18nKey: 'menu.classroomCreateExamPaper',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.exam-paper'],
        title: 'Question Bank',
        icon: 'ri:list-check-3',
        i18nKey: 'menu.classroomCreateExamPaper',
      },
    },
  },
  {
    path: '/classroom/exam-paper/:examPaperId/add-question-bank',
    name: 'classroom.add-question-bank',
    component: () => import('@/views/classroom/components/ExamPaper/ChooseQuestionBank.vue'),
    meta: {
      group: 'classroom',
      icon: 'ri:list-check-3',
      i18nKey: 'menu.classroomCreateExamPaper',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.exam-paper'],
        title: 'Question Bank',
        icon: 'ri:list-check-3',
        i18nKey: 'menu.classroomCreateExamPaper',
      },
    },
  },
  {
    path: '/classroom/exam-paper/:examPaperId/configure-score',
    name: 'classroom.configure-score',
    component: () => import('@/views/classroom/components/ExamPaper/ConfigureScore.vue'),
    meta: {
      group: 'classroom',
      icon: 'ion:settings-outline',
      i18nKey: 'menu.classroomConfigureScore',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.exam-paper'],
        title: 'Configure Score',
        icon: 'ion:settings-outline',
        i18nKey: 'menu.classroomConfigureScore',
      },
    },
  },

  // BRAND VOICES GROUP
  {
    path: '/brand-voices',
    name: 'brand-voices',
    component: () => import('@/views/brand-voice/index.vue'),
    meta: {
      group: 'brand-voices',
      icon: 'ri:mic-line',
      i18nKey: 'menu.brandVoices',
      breadcrumb: {
        parent: ['home'],
        title: 'Brand Voices',
        icon: 'ri:mic-line',
        i18nKey: 'menu.brandVoices',
      },
    },
  },

  // SETTINGS GROUP
  {
    path: '/workspace-settings/:tab?',
    name: 'workspace-settings',
    component: () => import('@/views/settings/index.vue'),
    meta: {
      group: 'settings',
      icon: 'ri:settings-line',
      i18nKey: 'settings.title',
      breadcrumb: {
        parent: ['home'],
        title: 'Settings',
        icon: 'ri:settings-line',
        i18nKey: 'settings.title',
        params: ['tab'],
      },
    },
  },
  {
    path: '/upgrade',
    name: 'upgrade-pricing',
    component: () => import('@/views/settings/UpgradePricing.vue'),
    meta: {
      group: 'settings',
      icon: 'ri:vip-crown-line',
      i18nKey: 'upgrade.title',
      breadcrumb: {
        parent: ['home'],
        title: 'Upgrade',
        icon: 'ri:vip-crown-line',
        i18nKey: 'upgrade.title',
      },
    },
  },
  {
    path: '/upgrade/checkout',
    name: 'upgrade-checkout',
    component: () => import('@/views/settings/UpgradeCheckout.vue'),
    meta: {
      group: 'settings',
      icon: 'ri:secure-payment-line',
      i18nKey: 'payment.title',
      breadcrumb: {
        parent: ['home', 'upgrade-pricing'],
        title: 'Checkout',
        icon: 'ri:secure-payment-line',
        i18nKey: 'payment.title',
      },
    },
  },
  {
    path: '/upgrade/result/success',
    name: 'upgrade-payment-success',
    component: () => import('@/views/settings/UpgradePaymentSuccess.vue'),
    meta: {
      group: 'settings',
      icon: 'ri:checkbox-circle-line',
      i18nKey: 'payment.success.title',
      breadcrumb: {
        parent: ['home'],
        title: 'Payment Success',
        icon: 'ri:checkbox-circle-line',
        i18nKey: 'payment.success.title',
      },
    },
  },
  {
    path: '/upgrade/result/failure',
    name: 'upgrade-payment-failure',
    component: () => import('@/views/settings/UpgradePaymentFailure.vue'),
    meta: {
      group: 'settings',
      icon: 'ri:close-circle-line',
      i18nKey: 'payment.failure.title',
      breadcrumb: {
        parent: ['home'],
        title: 'Payment Failure',
        icon: 'ri:close-circle-line',
        i18nKey: 'payment.failure.title',
      },
    },
  },
  {
    path: '/workspace-approve/',
    name: 'workspace-approve',
    component: () => import('@/views/settings/WorkspaceApprove/index.vue'),
    meta: {
      // group: 'settings',
      icon: 'ion:checkmark-circle-outline',
      i18nKey: 'settings.workspaceSettings.workspaceApprove',
      breadcrumb: {
        parent: ['home'],
        title: 'WorkspaceApprove',
        icon: 'ion:checkmark-circle-outline',
        i18nKey: 'settings.workspaceSettings.workspaceApprove',
      },
    },
  },
  {
    path: '/image-approve/',
    name: 'image-approve',
    component: () => import('@/views/settings/ImageApprove/index.vue'),
    meta: {
      // group: 'settings',
      icon: 'ion:checkmark-circle-outline',
      i18nKey: 'settings.workspaceSettings.imageApprove',
      breadcrumb: {
        parent: ['home'],
        title: 'Image Approve',
        icon: 'ion:checkmark-circle-outline',
        i18nKey: 'settings.workspaceSettings.imageApprove',
      },
    },
  },
  {
    path: '/parameter-configuration',
    name: 'parameter-configuration',
    component: () => import('@/views/parameter-config/index.vue'),
    meta: {
      // group: 'settings',
      icon: 'ri:grid-line',
      i18nKey: 'menu.parameterConfiguration',
      breadcrumb: {
        parent: ['home'],
        title: 'Parameter Configuration',
        icon: 'ri:grid-line',
        i18nKey: 'menu.parameterConfiguration',
      },
    },
  },

  {
    path: '/api-service-configuration',
    name: 'api-service-configuration',
    component: () => import('@/views/api-service-config/index.vue'),
    meta: {
      // group: 'settings',
      icon: 'ion:server-outline',
      i18nKey: 'menu.apiServiceConfiguration',
      breadcrumb: {
        parent: ['home'],
        title: 'API Service Configuration',
        icon: 'ion:server-outline',
        i18nKey: 'menu.apiServiceConfiguration',
      },
    },
  },
  {
    path: '/api-service-configuration/form',
    name: 'api-service-configuration-form',
    component: () => import('@/views/api-service-config/form.vue'),
    meta: {
      // group: 'settings',
      icon: 'ion:server-outline',
      i18nKey: 'menu.apiServiceConfiguration',
      breadcrumb: {
        parent: ['home', 'api-service-configuration'],
        title: 'Form',
        icon: 'ion:server-outline',
        i18nKey: 'menu.apiServiceConfiguration',
      },
    },
  },

  {
    path: '/ai-model/',
    name: 'ai-model',
    component: () => import('@/views/settings/AIModel/index.vue'),
    meta: {
      // group: 'settings',
      icon: 'ri:robot-line',
      i18nKey: 'settings.workspaceSettings.aiModel',
      breadcrumb: {
        parent: ['home'],
        title: 'Model AI',
        icon: 'ri:robot-line',
        i18nKey: 'settings.workspaceSettings.aiModel',
      },
    },
  },

  {
    path: '/notification-config/',
    name: 'notification-config',
    component: () => import('@/views/settings/NotificationConfig/index.vue'),
    meta: {
      // group: 'settings',
      icon: 'ion:notifications-outline',
      i18nKey: 'settings.workspaceSettings.notification',
      breadcrumb: {
        parent: ['home'],
        title: 'Notification Config',
        icon: 'ion:notifications-outline',
        i18nKey: 'settings.workspaceSettings.notification',
      },
    },
  },
  {
    path: '/system-settings/credit-bonus',
    name: 'credit-bonus',
    component: () => import('@/views/settings/SystemSettings/index.vue'),
    meta: {
      // group: 'settings',
      icon: 'ion:shield-half-outline',
      i18nKey: 'settings.workspaceSettings.systemSettings',
      breadcrumb: {
        parent: ['home'],
        title: 'System Settings',
        icon: 'ion:shield-half-outline',
        i18nKey: 'settings.workspaceSettings.systemSettings',
      },
    },
  },
  {
    path: '/system-settings/system-users',
    name: 'system-users',
    component: () => import('@/views/settings/SystemSettings/index.vue'),
    meta: {
      // group: 'settings',
      icon: 'ion:shield-half-outline',
      i18nKey: 'settings.workspaceSettings.systemSettings',
      breadcrumb: {
        parent: ['home'],
        title: 'System Settings',
        icon: 'ion:shield-half-outline',
        i18nKey: 'settings.workspaceSettings.systemSettings',
      },
    },
  },

  // USER GROUP
  {
    path: '/user/profile',
    name: 'user-profile',
    component: () => import('@/views/user/profileInfo/profile.vue'),
    meta: {
      group: 'user',
      icon: 'ri:user-line',
      i18nKey: 'menu.userProfile',
      breadcrumb: {
        parent: ['home'],
        title: 'User Profile',
        icon: 'ri:user-line',
        i18nKey: 'menu.userProfile',
      },
    },
  },

  // TRANSACTION GROUP
  {
    path: '/transaction',
    name: 'transaction',
    component: () => import('@/views/transaction/index.vue'),
    meta: {
      group: 'transaction',
      icon: 'ri:wallet-3-line',
      i18nKey: 'menu.transaction',
      breadcrumb: {
        parent: ['home'],
        title: 'Transaction',
        icon: 'ri:wallet-3-line',
        i18nKey: 'menu.transaction',
      },
    },
  },

  {
    path: '/transaction/upload',
    name: 'transaction-upload',
    component: () => import('@/views/transaction/upload/index.vue'),
    meta: {
      group: 'transaction',
      icon: 'ri:wallet-3-line',
      i18nKey: 'menu.transaction',
      breadcrumb: {
        parent: ['home', 'transaction'],
        title: 'Upload',
        icon: 'ri:wallet-3-line',
        i18nKey: 'menu.transactionUpload',
      },
    },
  },

  // HELP & COMMUNITY GROUP
  {
    path: '/user-guide',
    name: 'user-guide',
    component: () => import('@/views/help-community/user-guide/index.vue'),
    meta: {
      group: 'help-community',
      icon: 'ri:file-info-line',
      i18nKey: 'menu.userGuide',
      breadcrumb: {
        parent: ['home'],
        title: 'User Guide',
        icon: 'ri:file-info-line',
        i18nKey: 'menu.userGuide',
      },
    },
  },
  {
    path: '/forum',
    name: 'forum',
    component: () => import('@/views/help-community/forum/index.vue'),
    meta: {
      group: 'help-community',
      icon: 'ri:chat-1-line',
      i18nKey: 'menu.forum',
      breadcrumb: {
        parent: ['home'],
        title: 'Forum',
        icon: 'ri:chat-1-line',
        i18nKey: 'menu.forum',
      },
    },
  },
  {
    path: '/contact-support',
    name: 'contact-support',
    component: () => import('@/views/help-community/contact-support/index.vue'),
    meta: {
      group: 'help-community',
      icon: 'ri:customer-service-2-line',
      i18nKey: 'menu.contactSupport',
      breadcrumb: {
        parent: ['home'],
        title: 'Contact Support',
        icon: 'ri:customer-service-2-line',
        i18nKey: 'menu.contactSupport',
      },
    },
  },

  // E-LEARNING GROUP
  {
    path: '/elearning',
    name: 'elearning',
    component: () => import('@/views/elearning/index.vue'),
    meta: {
      group: 'elearning',
      icon: 'ri:graduation-cap-line',
      i18nKey: 'menu.elearning',
      breadcrumb: {
        parent: ['home'],
        title: 'E-Learning',
        icon: 'ri:graduation-cap-line',
        i18nKey: 'menu.elearning',
      },
    },
  },

  // SLIDE GROUP
  {
    path: '/slide',
    name: 'slide',
    component: () => import('@/views/slide/index.vue'),
    meta: {
      group: 'slide',
      icon: 'ri:slideshow-line',
      i18nKey: 'menu.slide',
      breadcrumb: {
        parent: ['home'],
        title: 'Slide',
        icon: 'ri:slideshow-line',
        i18nKey: 'menu.slide',
      },
    },
  },

  // WRITER GROUP
  {
    path: '/writer',
    name: 'writer',
    component: () => import('@/views/writer/index.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:edit-line',
      i18nKey: 'menu.writer',
      breadcrumb: {
        parent: ['home'],
        title: 'Writer',
        icon: 'ri:edit-line',
        i18nKey: 'menu.writer',
      },
    },
  },

  {
    path: '/writer/:id',
    name: 'writer-update',
    component: () => import('@/views/writer/components/detail/index.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:file-edit-line',
      i18nKey: 'menu.writerUpdate',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Edit Writer',
        icon: 'ri:file-edit-line',
        i18nKey: 'menu.writerUpdate',
        params: ['id'],
      },
    },
  },

  {
    path: '/writer/templates',
    name: 'writer-templates',
    component: () => import('@/views/writer/components/template/temp/TemplateLibrary.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:file-copy-line',
      i18nKey: 'menu.writerTemplates',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Template Library',
        icon: 'ri:file-copy-line',
        i18nKey: 'menu.writerTemplates',
      },
    },
  },

  {
    path: '/writer/upload-template',
    name: 'writer-upload-template',
    component: () => import('@/views/writer/components/template/temp/UploadTemplatePage.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:upload-cloud-line',
      i18nKey: 'menu.writerUploadTemplate',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Tải lên template',
        icon: 'ri:upload-cloud-line',
        i18nKey: 'menu.writerUploadTemplate',
      },
    },
  },
  {
    path: '/writer/library',
    name: 'writer-library',
    component: () => import('@/views/writer/components/template/content/ContentLibrary.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:book-line',
      i18nKey: 'menu.writerLibrary',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Library',
        icon: 'ri:book-line',
        i18nKey: 'menu.writerLibrary',
      },
    },
  },
  {
    path: '/writer/upload-content',
    name: 'writer-upload-content',
    component: () => import('@/views/writer/components/template/content/UploadContentPage.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:upload-cloud-line',
      i18nKey: 'menu.writerUploadTemplate',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Tải lên nội dung',
        icon: 'ri:upload-cloud-line',
        i18nKey: 'menu.writerUploadTemplate',
      },
    },
  },
  {
    path: '/writer/blank-template',
    name: 'writer-blank-template',
    component: () => import('@/views/writer/components/template/content/BlankTemplateEditor.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:file-add-line',
      i18nKey: 'menu.writerBlankTemplate',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Tạo template trống',
        icon: 'ri:file-add-line',
        i18nKey: 'menu.writerBlankTemplate',
      },
    },
  },
  {
    path: '/writer/edit-template/:id',
    name: 'writer-edit-template',
    component: () => import('@/views/writer/components/template/content/BlankTemplateEditor.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:file-edit-line',
      i18nKey: 'menu.writerEditTemplate',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Chỉnh sửa template',
        icon: 'ri:file-edit-line',
        i18nKey: 'menu.writerEditTemplate',
      },
    },
  },
  {
    path: '/writer/template-selector',
    name: 'writer-template-selector',
    component: () => import('@/views/writer/components/template/temp/TemplateLibrary.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.writerTemplateSelector',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Chọn Template',
        icon: 'ri:file-text-line',
        i18nKey: 'menu.writerTemplateSelector',
      },
    },
  },
  {
    path: '/writer/content-library',
    name: 'writer-content-library',
    component: () => import('@/views/writer/components/template/content/ContentLibrary.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:folder-line',
      i18nKey: 'menu.writerContentLibrary',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Content Library',
        icon: 'ri:folder-line',
        i18nKey: 'menu.writerContentLibrary',
      },
    },
  },
  {
    path: '/writer/blank-content',
    name: 'writer-blank-content',
    component: () => import('@/views/writer/components/template/list-content/BlankContentEditor.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:file-add-line',
      i18nKey: 'menu.writerBlankContent',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Tạo nội dung trống',
        icon: 'ri:file-add-line',
        i18nKey: 'menu.writerBlankContent',
      },
    },
  },
  {
    path: '/writer/edit-content/:id',
    name: 'writer-edit-content',
    component: () => import('@/views/writer/components/template/list-content/BlankContentEditor.vue'),
    meta: {
      group: 'writer',
      icon: 'ri:file-edit-line',
      i18nKey: 'menu.writerEditContent',
      breadcrumb: {
        parent: ['home', 'writer'],
        title: 'Chỉnh sửa nội dung',
        icon: 'ri:file-edit-line',
        i18nKey: 'menu.writerEditContent',
      },
    },
  },
  {
    path: '/classroom/:classroomId/subject/:subjectId/flashcards/:chapterId',
    name: 'classroom.flashcardPractice',
    component: () => import('@/views/classroom/components/Subject/FlashcardPractice.vue'),
    meta: {
      group: 'classroomFlashcardPractice',
      icon: 'mdi:cards-outline',
      i18nKey: 'menu.classroomFlashcardPractice',
      breadcrumb: {
        parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
        title: 'Flashcard Practice',
        icon: 'mdi:cards-outline',
        i18nKey: 'menu.classroomFlashcardPractice',
        params: ['classroomId', 'subjectId', 'chapterId'],
      },
    },
  },

  // MIND MAP
  {
    path: '/mind-map',
    name: 'mind-map',
    component: () => import('@/views/mindmap/index.vue'),
    meta: {
      group: 'mind-map',
      icon: 'ri:mind-map',
      i18nKey: 'menu.mindmap',
      breadcrumb: {
        parent: ['home'],
        title: 'Sơ đồ tư duy AI',
        icon: 'ri:mind-map',
        i18nKey: 'menu.mindmap',
      },
    },
  },

  // MIND MAP DETAIL
  {
    path: '/mind-map/detail',
    name: 'mind-map-detail',
    component: () => import('@/views/mindmap/components/MindMapDetail.vue'),
    meta: {
      group: 'mind-map',
      icon: 'ri:mind-map',
      i18nKey: 'menu.detailMindmap',
      breadcrumb: {
        parent: ['mind-map'],
        title: 'Chi tiết sơ đồ tư duy',
        icon: 'ri:mind-map',
        i18nKey: 'menu.detailMindmap',
      },
    },
  },

  // CHAT PDF
  {
    path: '/agent-pdf',
    name: 'chat-pdf',
    component: () => import('@/views/agent-pdf/index.vue'),
    meta: {
      group: 'agent-pdf',
      icon: 'ri:file-pdf-line',
      i18nKey: 'menu.agentPdf',
      breadcrumb: {
        parent: ['home'],
        title: 'menu.agentPdf',
        icon: 'ri:file-pdf-line',
        i18nKey: 'menu.agentPdf',
      },
    },
  },
  {
    path: '/agent-pdf/:docId',
    name: 'chat-pdf.detail',
    component: () => import('@/views/agent-pdf/detail.vue'),
    meta: {
      group: 'agent-pdf',
      icon: 'ri:file-pdf-line',
      i18nKey: 'menu.agentPdfDetail',
      breadcrumb: {
        parent: ['home', 'chat-pdf'],
        title: 'menu.agentPdfDetail',
        icon: 'ri:file-pdf-line',
        i18nKey: 'menu.agentPdfDetail',
      },
    },
  },

  // WHITEBOARD
  {
    path: '/whiteboard',
    name: 'Whiteboard',
    component: () => import('@/views/whiteboard/index.vue'),
    meta: {
      group: 'whiteboard',
      icon: 'ri:draft-line',
      i18nKey: 'menu.whiteboard',
      requiresAuth: true,
      breadcrumb: {
        parent: ['home'],
        title: 'menu.whiteboard',
        icon: 'ri:draft-line',
        i18nKey: 'menu.whiteboard',
      },
    },
  },

  // IMAGE TO TEXT
  {
    path: '/image-to-word',
    name: 'image-to-word',
    component: () => import('@/views/OCR/image-to-word/index.vue'),
    meta: {
      group: 'image-to-word',
      icon: 'ri:image-line',
      i18nKey: 'menu.imageToWord',
      breadcrumb: {
        parent: ['home'],
        title: 'Image To Word',
        icon: 'ri:image-line',
        i18nKey: 'menu.imageToWord',
      },
    },
  },

  // PDF TO TEXT
  {
    path: '/pdf-to-word',
    name: 'pdf-to-word',
    component: () => import('@/views/OCR/pdf-to-word/index.vue'),
    meta: {
      group: 'pdf-to-word',
      icon: 'ri:file-pdf-2-line',
      i18nKey: 'menu.pdfToWord',
      breadcrumb: {
        parent: ['home'],
        title: 'PDF To Word',
        icon: 'ri:file-pdf-2-line',
        i18nKey: 'menu.pdfToWord',
      },
    },
  },

  {
    path: '/document/:documentId',
    name: 'document.detail',
    component: () => import('@/views/OCR/document/Detail.vue'),
    meta: {
      group: 'document',
      icon: 'ri:file-edit-line',
      i18nKey: 'menu.document',
      breadcrumb: {
        parent: ['home'],
        title: 'document',
        icon: 'ri:file-edit-line',
        i18nKey: 'menu.document',
      },
    },
  },

  {
    path: '/classroom/system-settings/textbook',
    name: 'classroom.system-settings.textbook',
    component: () => import('@/views/classroom-v2/components/SystemSettings/index.vue'),
    meta: {
      group: 'classroom-teacher-system-settings',
      title: 'System Settings',
    },
  },
  {
    path: '/classroom/system-settings/teacher-book',
    name: 'classroom.system-settings.teacher-book',
    component: () => import('@/views/classroom-v2/components/SystemSettings/index.vue'),
    meta: {
      // group: 'classroom-teacher-system-settings',
      title: 'System Settings',
    },
  },
  {
    path: '/classroom/system-settings/decree-circular',
    name: 'classroom.system-settings.decree-circular',
    component: () => import('@/views/classroom-v2/components/SystemSettings/index.vue'),
    meta: {
      // group: 'classroom-teacher-system-settings',
      title: 'System Settings',
    },
  },
  {
    path: '/classroom/system-settings/tool-management',
    name: 'classroom.system-settings.tool-management',
    component: () => import('@/views/classroom-v2/components/SystemSettings/index.vue'),
    meta: {
      // group: 'classroom-teacher-system-settings',
      title: 'System Settings',
    },
  },
  {
    path: '/classroom/system-settings/tool-management/template-content/:configId',
    name: 'classroom.system-settings.toolManagement.templateContent',
    component: () => import('@/views/classroom-v2/components/SystemSettings/ToolManagement/template-content.vue'),
    meta: {
      // group: 'classroom-teacher-system-settings',
      title: 'Template Content',
    },
  },
]

export const ROUTE_TEACHER_CONFIGS: RouteConfig[] = [
  {
    path: '/classroom/teacher/list',
    name: 'classroom.teacher.list',
    component: () => import('@/views/classroom-v2/components/Teacher/ClassroomList/index.vue'),
    meta: {
      group: 'classroom-teacher-list',
      title: 'Danh sách lớp học',
      i18nKey: 'menu.classroomList',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/board',
    name: 'classroom.teacher.board',
    component: () => import('@/views/classroom-v2/components/Teacher/TeachBoard.vue'),
    meta: {
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/lesson-plan-generator',
    name: 'classroom.teacher.lesson-plan-generator',
    component: () => import('@/views/classroom-v2/components/LessonPlanGenerator/index.vue'),
    meta: {
      title: 'classroom',
      breadcrumb: {
        parent: ['classroom.teacher.board'],
        title: 'Soạn kế hoạch bài dạy',
        icon: 'ri:file-edit-line',
        params: ['classroomId'],
      },
    },
  },
  {
    path: '/classroom/teacher/:classroomId/chat',
    name: 'classroom.teacher.chat',
    component: () => import('@/views/classroom-v2/components/Teacher/FullScreenChat.vue'),
    meta: {
      title: 'classroom',
      breadcrumb: {
        parent: ['classroom.teacher.board'],
        title: 'Chat',
        icon: 'ri:message-3-line',
        params: ['classroomId'],
      },
    },
  },
  // {
  //   path: '/classroom/teacher/:classroomId/chat-pdf',
  //   name: 'classroom.teacher.chat-pdf',
  //   component: () => import('@/views/classroom-v2/components/Teacher/ChatPdfCard.vue'),
  //   meta: {
  //     title: 'classroom',
  //     breadcrumb: {
  //       parent: ['classroom.teacher.board'],
  //       title: 'Chat PDF',
  //       icon: 'ri:file-pdf-line',
  //       params: ['classroomId'],
  //     },
  //   },
  // },
  {
    path: '/classroom/teacher/:classroomId/agent-pdf/:docId',
    name: 'classroom.teacher.agent-pdf-detail',
    component: () => import('@/views/agent-pdf/detail.vue'),
    meta: {
      title: 'classroom',
      breadcrumb: {
        parent: ['classroom.teacher.chat-pdf'],
        title: 'PDF Detail',
        icon: 'ri:file-pdf-line',
        params: ['classroomId', 'docId'],
      },
    },
  },
  {
    path: '/classroom-manage',
    name: 'classroom-manage',
    component: () => import('@/views/classroom-v2/components/Manage/index.vue'),
    meta: {
      group: 'classroom-manage',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId',
    name: 'classroom.teacher.detail',
    component: () => import('@/views/classroom-v2/components/Teacher/index.vue'),
    meta: {
      group: 'classroom-teacher-detail',
      title: 'classroom',
    },
  },

  {
    path: '/classroom/teacher/:classroomId/writer/:id',
    name: 'classroom.teacher.writer',
    component: () => import('@/views/writer/components/detail/index.vue'),
    meta: {
      group: 'classroom-teacher-writer',
      icon: 'ri:file-edit-line',
      i18nKey: 'menu.writerUpdate',
      breadcrumb: {
        parent: ['classroom.teacher.board'],
        title: 'Edit Writer',
        icon: 'ri:file-edit-line',
        i18nKey: 'menu.writerUpdate',
        params: ['classroomId', 'id'],
      },
    },
  },
  {
    path: '/classroom/teacher/:classroomId/pdf-to-text',
    name: 'classroom.teacher.pdfToText',
    component: () => import('@/views/classroom-v2/components/Teacher/PdfToTextCard.vue'),
    meta: {
      group: 'classroom-teacher-pdf-to-text',
      icon: 'ri:file-pdf-line',
      i18nKey: 'menu.pdfToWord',
      breadcrumb: {
        parent: ['classroom.teacher.board'],
        title: 'PDF To Text',
        icon: 'ri:file-pdf-line',
        i18nKey: 'menu.pdfToWord',
        params: ['classroomId'],
      },
    },
  },
  {
    path: '/classroom/teacher/:classroomId/image-generation',
    name: 'classroom.teacher.image-generation',
    component: () => import('@/views/image-generation/index.vue'),
    meta: {
      group: 'classroom-teacher-image-generation',
      icon: 'ri:multi-image-line',
      i18nKey: 'menu.imageGeneration',
      breadcrumb: {
        parent: ['classroom.teacher.board'],
        title: 'Image Generation',
        icon: 'ri:multi-image-line',
        i18nKey: 'menu.imageGeneration',
        params: ['classroomId'],
      },
    },
  },
  {
    path: '/classroom/teacher/:classroomId/mind-map',
    name: 'classroom.teacher.mind-map',
    component: () => import('@/views/mindmap/components/MindMapDetail.vue'),
    meta: {
      group: 'classroom-teacher-mind-map',
      icon: 'ri:mind-map',
      i18nKey: 'menu.mindmap',
      breadcrumb: {
        parent: ['classroom.teacher.board'],
        title: 'Mindmap',
        icon: 'ri:mind-map',
        i18nKey: 'menu.mindmap',
        params: ['classroomId'],
      },
    },
  },
  {
    path: '/classroom/teacher/:classroomId/image-to-text',
    name: 'classroom.teacher.imageToText',
    component: () => import('@/views/classroom-v2/components/Teacher/ImageToTextCard.vue'),
    meta: {
      group: 'classroom-teacher-image-to-text',
      icon: 'ri:image-line',
      i18nKey: 'menu.imageToWord',
      breadcrumb: {
        parent: ['classroom.teacher.board'],
        title: 'Image To Text',
        icon: 'ri:image-line',
        i18nKey: 'menu.imageToWord',
        params: ['classroomId'],
      },
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject',
    name: 'classroom.teacher.subject',
    component: () => import('@/views/classroom-v2/components/Subject/index.vue'),
    meta: {
      // group: 'classroom-teacher-detail',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId',
    name: 'classroom.teacher.subject.detail',
    component: () => import('@/views/classroom-v2/components/Subject/Detail.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/member',
    name: 'classroom.teacher.member',
    component: () => import('@/views/classroom-v2/components/Member/index.vue'),
    meta: {
      group: 'classroom-teacher-member',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/result',
    name: 'classroom.teacher.result',
    component: () => import('@/views/classroom-v2/components/Result/index.vue'),
    meta: {
      group: 'classroom-teacher-result',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/result/:studentId',
    name: 'classroom.teacher.result.detail',
    component: () => import('@/views/classroom-v2/components/Result/Detail.vue'),
    meta: {
      group: 'classroom-teacher-result',
      title: 'classroom',
    },
  },

  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/assign-agent',
    name: 'classroom.teacher.assign-agent',
    component: () => import('@/views/classroom-v2/components/AgentSupport/AssignAgent.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },

  {
    path: '/classroom/teacher/:classroomId/assign-agent-classroom',
    name: 'classroom.teacher.assign-agent-classroom',
    component: () => import('@/views/classroom-v2/components/AgentSupport/AssignAgentClassroom.vue'),
    meta: {
      group: 'classroom-teacher-detail',
      title: 'classroom',
    },
  },

  {
    path: '/classroom/teacher/:classroomId/agents',
    name: 'classroom.teacher.add-agent-classroom',
    component: () => import('@/views/classroom-v2/components/AgentSupport/AgentSupportList.vue'),
    meta: {
      group: 'classroom-teacher-detail',
      title: 'classroom',
    },
  },

  // Learning Resources Route
  {
    path: '/classroom/teacher/:classroomId/learning-resources',
    name: 'classroom.teacher.learning-resources',
    component: () => import('@/views/classroom-v2/components/LearningResources/index.vue'),
    meta: {
      group: 'classroom-teacher-learning-resources',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/learning-resources/:resourceId',
    name: 'classroom.teacher.learning-resources.detail',
    component: () => import('@/views/classroom-v2/components/LearningResources/detail.vue'),
    meta: {
      group: 'classroom-teacher-learning-resources',
      title: 'classroom',
    },
  },

  // Lesson and Document Routes
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/lesson/:lessonId/upload-doc',
    name: 'classroom.teacher.document.upload',
    component: () => import('@/views/classroom-v2/components/Content/UploadDocument.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/lesson/:lessonId/upload-doc-tool',
    name: 'classroom.teacher.document.uploadTool',
    component: () => import('@/views/classroom-v2/components/Content/SelectFromTool.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },

  // Practice Routes
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/lesson/:lessonId/practice-editor',
    name: 'classroom.teacher.practice.editor',
    component: () => import('@/views/classroom-v2/components/Content/CreatePractice.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/practice-exercise/:exerciseId',
    name: 'classroom.teacher.practiceExercise',
    component: () => import('@/views/classroom-v2/components/Subject/PracticeExercise.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },

  // Flashcard Routes
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/lesson/:lessonId/flashcard-editor',
    name: 'classroom.teacher.flashcard.editor',
    component: () => import('@/views/classroom-v2/components/Content/CreateFlashcard.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },

  // Exercise Routes
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/lesson/:lessonId/exercise-editor',
    name: 'classroom.teacher.exercise.editor',
    component: () => import('@/views/classroom-v2/components/Content/CreateExercise.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  // {
  //   path: '/classroom/teacher/:classroomId/create-exercise',
  //   name: 'classroom.teacher.createExercise',
  //   component: () => import('@/views/classroom-v2/components/Exercise/Index.vue'),
  //   meta: {
  //     group: 'classroomTeacherCreateExercise',
  //     icon: 'ri:edit-2-line',
  //     i18nKey: 'menu.classroomCreateExercise',
  //     breadcrumb: {
  //       parent: ['home', 'classroom.teacher.detail'],
  //       title: 'Create Exercise',
  //       icon: 'ri:edit-2-line',
  //       i18nKey: 'menu.classroomCreateExercise',
  //     },
  //   },
  // },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/exercise/:exerciseId',
    name: 'classroom.teacher.exerciseDetail',
    component: () => import('@/views/classroom-v2/components/Subject/Exercise/ExerciseDetail.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/exercise-progress/:exerciseId',
    name: 'classroom.teacher.exerciseProgressDetail',
    component: () => import('@/views/classroom-v2/components/Subject/Exercise/ExerciseProgressDetail.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/exercise/:exerciseId/student/:studentId/result',
    name: 'classroom.teacher.studentExerciseResult',
    component: () => import('@/views/classroom-v2/components/Subject/components/screens/StudentExerciseResult.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },

  // Teaching Plan Routes
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/upload-teaching-plan',
    name: 'classroom.teacher.uploadTeachingPlan',
    component: () => import('@/views/classroom-v2/components/Subject/components/uploads/UploadTeachingPlan.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/upload-teaching-plan-tool',
    name: 'classroom.teacher.uploadTeachingPlanTool',
    component: () => import('@/views/classroom-v2/components/Subject/components/uploads/UploadTeachingPlanTool.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },

  // Lesson Plan Routes
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/upload-lesson-plan',
    name: 'classroom.teacher.uploadLessonPlan',
    component: () => import('@/views/classroom-v2/components/Subject/components/uploads/UploadLessonPlan.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/upload-lesson-plan-tool',
    name: 'classroom.teacher.uploadLessonPlanTool',
    component: () => import('@/views/classroom-v2/components/Subject/components/uploads/UploadLessonPlanTool.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },

  // Document Detail Route
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/document/:documentId',
    name: 'classroom.teacher.document.detail',
    component: () => import('@/views/classroom-v2/components/Subject/DocumentDetail.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },

  // Exam Progress Routes
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/exam-progress/:examId',
    name: 'classroom.teacher.examProgressDetail',
    component: () => import('@/views/classroom-v2/components/Subject/Exam/ExamProgressDetail.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/exam/:examId/student/:studentId/result',
    name: 'classroom.teacher.studentExamResult',
    component: () => import('@/views/classroom-v2/components/Subject/components/screens/StudentExamResult.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/practice-exercise/:exerciseId',
    name: 'classroom.teacherPracticeExercise',
    component: () => import('@/views/classroom-v2/components/Subject/PracticeExercise.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/subject/:subjectId/exam/:examId/grade/:submitId',
    name: 'classroom.teacher.exam.grade',
    component: () => import('@/views/classroom-v2/components/Subject/components/screens/ExamGradeView.vue'),
    meta: {
      group: 'classroom-teacher-subject',
      title: 'classroom',
    },
  },
  // {
  //   path: '/classroom/teacher/:classroomId/subject/:subjectId/exercise/:exerciseId',
  //   name: 'classroom.teacher.exerciseDetail',
  //   component: () => import('@/views/classroom-v2/components/Subject/ExcerciseDetail.vue'),
  //   meta: {
  //     group: 'classroomTeacherExerciseDetail',
  //     icon: 'ri:edit-2-line',
  //     i18nKey: 'menu.classroomTeacherExerciseDetail',
  //     breadcrumb: {
  //       parent: ['home', 'classroom', 'classroom.detail', 'classroom.subject'],
  //       title: 'Exercise Detail',
  //       icon: 'ri:edit-2-line',
  //       i18nKey: 'menu.classroomExerciseDetail',
  //     },
  //   },
  // },

  {
    path: '/classroom/teacher/:classroomId/exam-paper',
    name: 'classroom.teacher.exam-paper',
    component: () => import('@/views/classroom-v2/components/ExamPaper/ExamPaper.vue'),
    meta: {
      group: 'classroom-teacher-exam-paper',
      title: 'Exam Paper',
    },
  },

  {
    path: '/classroom/teacher/:classroomId/exam-paper/:examPaperId/add-question-ai',
    name: 'classroom.teacher.add-question-ai',
    component: () => import('@/views/classroom-v2/components/ExamPaper/AutoAIGeneration.vue'),
    meta: {
      group: 'classroom-teacher-exam-paper',
      title: 'Exam Paper',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/exam-paper/:examPaperId/add-question-bank',
    name: 'classroom.teacher.add-question-bank',
    component: () => import('@/views/classroom-v2/components/ExamPaper/ChooseQuestionBank.vue'),
    meta: {
      group: 'classroom-teacher-exam-paper',
      title: 'Exam Paper',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/exam-paper/:examPaperId/configure-score',
    name: 'classroom.teacher.configure-score',
    component: () => import('@/views/classroom-v2/components/ExamPaper/ConfigureScore.vue'),
    meta: {
      group: 'classroom-teacher-exam-paper',
      title: 'Exam Paper',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/exam-paper/:examPaperId/edit-questions',
    name: 'classroom.teacher.edit-questions',
    component: () => import('@/views/classroom-v2/components/ExamPaper/EditQuestionForExamPaper.vue'),
    meta: {
      group: 'classroom-teacher-exam-paper',
      title: 'Edit Questions',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/exam',
    name: 'classroom.teacher.exam',
    component: () => import('@/views/classroom-v2/components/ExamPaper/Exam.vue'),
    meta: {
      title: 'Exam',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/question-bank',
    name: 'classroom.teacher.question-bank',
    component: () => import('@/views/classroom-v2/components/ExamPaper/QuestionBank.vue'),
    meta: {
      group: 'classroom-teacher-question-bank',
      title: 'Question Bank',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/question-bank/:questionBankId',
    name: 'classroom.teacher.question-bank.detail',
    component: () => import('@/views/classroom-v2/components/ExamPaper/QuestionBankDetail.vue'),
    meta: {
      group: 'classroom-teacher-question-bank',
      title: 'Question Bank',
    },
  },

  // Start Lesson Routes
  {
    path: '/classroom/teacher/:classroomId/start-lesson',
    name: 'classroom.teacher.start-lesson',
    component: () => import('@/views/classroom-v2/components/Teacher/StartLesson/SubjectSelection.vue'),
    meta: {
      title: 'Start Lesson',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/start-lesson/:subjectId',
    name: 'classroom.teacher.start-lesson.subject',
    component: () => import('@/views/classroom-v2/components/Teacher/StartLesson/LessonList.vue'),
    meta: {
      title: 'Lesson List',
    },
  },
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/practice-exercise/:exerciseId',
    name: 'classroom.student.practiceExercise',
    component: () => import('@/views/classroom-v2/components/Subject/PracticeExercise.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/flashcards/:chapterId',
    name: 'classroom.student.flashcardPractice',
    component: () => import('@/views/classroom-v2/components/Subject/FlashcardPractice.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/exercise/:exerciseId',
    name: 'classroom.student.exerciseDetail',
    component: () => import('@/views/classroom-v2/components/Subject/Exercise/ExerciseDetail.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/document/:documentId',
    name: 'classroom.teacher.ocr.document.detail',
    component: () => import('@/views/OCR/document/Detail.vue'),
    meta: {
      group: 'document',
      icon: 'ri:file-edit-line',
      i18nKey: 'menu.document',
      breadcrumb: {
        parent: ['home'],
        title: 'document',
        icon: 'ri:file-edit-line',
        i18nKey: 'menu.document',
      },
    },
  },

  // agent
  {
    path: '/classroom/teacher/:classroomId/agent',
    name: 'classroom.teacher.agent',
    component: () => import('@/views/classroom-v2/components/Agent/index.vue'),
    meta: {
      group: 'classroom-teacher-agent',
      title: 'Agent',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/agent/editor',
    name: 'classroom.teacher.agent.editor',
    component: () => import('@/views/classroom-v2/components/Agent/AgentEditor.vue'),
    meta: {
      group: 'classroom-teacher-agent',
      title: 'Agent Editor',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/agent/share/:agentId',
    name: 'classroom.teacher.agent.share',
    component: () => import('@/views/classroom-v2/components/Agent/ShareUrl.vue'),
    meta: {
      group: 'classroom-teacher-agent',
      title: 'Agent',
    },
  },

  {
    path: '/classroom/agent/:agentId',
    name: 'classroom.teacher.agent.chat',
    component: () => import('@/views/classroom-v2/components/Agent/Chat/index.vue'),
    meta: {
      group: 'classroom-teacher-agent',
      title: 'Agent',
    },
  },
  {
    path: '/classroom/teacher/:classroomId/agent/interface/:agentId',
    name: 'classroom.teacher.agent.interface',
    component: () => import('@/views/classroom-v2/components/Agent/Interface.vue'),
    meta: {
      group: 'classroom-teacher-agent',
      title: 'Agent',
    },
  },
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/document/:documentId',
    name: 'classroom.student.document.detail',
    component: () => import('@/views/classroom-v2/components/Subject/DocumentDetail.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  // {
  //   path: '/classroom/teacher/:classroomId/system-settings',
  //   name: 'classroom.teacher.system-settings',
  //   component: () => import('@/views/classroom-v2/components/SystemSettings/index.vue'),
  //   meta: {
  //     group: 'classroom-teacher-system-settings',
  //     title: 'System Settings',
  //   },
  // },
  // {
  //   path: '/classroom/teacher/:classroomId/system-settings/tool-management/template-content/:configId',
  //   name: 'classroom.teacher.system-settings.toolManagement.templateContent',
  //   component: () => import('@/views/classroom-v2/components/SystemSettings/ToolManagement/template-content.vue'),
  //   meta: {
  //     group: 'classroom-teacher-system-settings',
  //     title: 'Template Content',
  //   },
  // },
  {
    path: '/classroom/teacher/:classroomId/elearning',
    name: 'classroom.teacher.elearning',
    component: () => import('@/views/elearning/index.vue'),
    meta: {
      group: 'classroom-teacher-elearning',
      icon: 'ri:graduation-cap-line',
      i18nKey: 'menu.aiElearning',
      breadcrumb: {
        parent: ['classroom.teacher.board'],
        title: 'AI E-Learning',
        icon: 'ri:graduation-cap-line',
        i18nKey: 'menu.aiElearning',
        params: ['classroomId'],
      },
    },
  },
  {
    path: 'classroom/workspace',
    name: 'classroom.workspace',
    component: () => import('@/views/workspace-settings/index.vue'),
    meta: {
      title: 'workspace',
    },
  },
  {
    path: 'classroom/teacher/plan-storage',
    name: 'classroom.teacher.plan-storage',
    component: () => import('@/views/settings/PackageAndStorage/index.vue'),
    meta: {
      title: 'Package Storage',
    },
  },
  {
    path: 'classroom/teacher/knowledge/:knowledgeId/:documentId',
    name: 'classroom.teacher.knowledge.detail',
    component: () => import('@/views/settings/PackageAndStorage/Knowledge/DetailLayout.vue'),
    meta: {
      title: 'Package Storage',
    },
  },
  {
    path: 'classroom/teacher/knowledge/:knowledgeId/:documentId/settings',
    name: 'classroom.teacher.knowledge.setting',
    component: () => import('@/views/settings/PackageAndStorage/Knowledge/Settings.vue'),
    meta: {
      title: 'Package Storage',
    },
  },
]

export const ROUTE_STUDENT_CONFIGS: RouteConfig[] = [

  {
    path: '/classroom/student/:classroomId/board',
    name: 'classroom.student.board',
    component: () => import('@/views/classroom-v2/components/Student/TeachBoard.vue'),
    meta: {
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/agent',
    name: 'classroom.student.agent',
    component: () => import('@/views/classroom-v2/components/Agent/index.vue'),
    meta: {
      group: 'classroom-teacher-agent',
      title: 'Agent Editor',
    },
  },
  {
    path: '/classroom/student/:classroomId/agent/editor',
    name: 'classroom.student.agent.editor',
    component: () => import('@/views/classroom-v2/components/Agent/AgentEditor.vue'),
    meta: {
      group: 'classroom-teacher-agent',
      title: 'Agent Editor',
    },
  },
  {
    path: '/classroom/student/:classroomId',
    name: 'classroom.student.detail',
    component: () => import('@/views/classroom-v2/components/Student/index.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  // Subject Selection Routes
  {
    path: '/classroom/student/:classroomId/document',
    name: 'classroom.student.document',
    component: () => import('@/views/classroom-v2/components/Student/SubjectSelection.vue'),
    meta: {
      group: 'classroom-student-document',
      title: 'classroom',
      functionType: 'document',
    },
  },
  {
    path: '/classroom/student/:classroomId/flashcard',
    name: 'classroom.student.flashcard',
    component: () => import('@/views/classroom-v2/components/Student/SubjectSelection.vue'),
    meta: {
      group: 'classroom-student-flashcard',
      title: 'classroom',
      functionType: 'flashcard',
    },
  },
  {
    path: '/classroom/student/:classroomId/practice',
    name: 'classroom.student.practice',
    component: () => import('@/views/classroom-v2/components/Student/SubjectSelection.vue'),
    meta: {
      group: 'classroom-student-practice',
      title: 'classroom',
      functionType: 'practice',
    },
  },
  {
    path: '/classroom/student/:classroomId/exercise',
    name: 'classroom.student.exercise',
    component: () => import('@/views/classroom-v2/components/Student/SubjectSelection.vue'),
    meta: {
      group: 'classroom-student-exercise',
      title: 'classroom',
      functionType: 'exercise',
    },
  },
  {
    path: '/classroom/student/:classroomId/exam',
    name: 'classroom.student.examTab',
    component: () => import('@/views/classroom-v2/components/Student/SubjectSelection.vue'),
    meta: {
      group: 'classroom-student-examTab',
      title: 'classroom',
      functionType: 'exam',
    },
  },
  {
    path: '/classroom/student/:classroomId/subject/:subjectId',
    name: 'classroom.student.subject.detail',
    component: () => import('@/views/classroom-v2/components/Subject/Detail.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/member',
    name: 'classroom.student.member',
    component: () => import('@/views/classroom-v2/components/Member/index.vue'),
    meta: {
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/result',
    name: 'classroom.student.result',
    component: () => import('@/views/classroom-v2/components/Result/index.vue'),
    meta: {
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/result/:studentId',
    name: 'classroom.student.result.detail',
    component: () => import('@/views/classroom-v2/components/Result/StudentDetail.vue'),
    meta: {
      title: 'classroom',
    },
  },

  // Document Routes
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/document/:documentId',
    name: 'classroom.student.document.detail',
    component: () => import('@/views/classroom-v2/components/Subject/DocumentDetail.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },

  // Practice Routes
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/practice-exercise/:exerciseId',
    name: 'classroom.student.practiceExercise',
    component: () => import('@/views/classroom-v2/components/Subject/PracticeExercise.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },

  // Exercise Routes
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/exercise/:exerciseId',
    name: 'classroom.student.exerciseDetail',
    component: () => import('@/views/classroom-v2/components/Subject/Exercise/ExerciseDetail.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/exercise-progress/:exerciseId',
    name: 'classroom.student.exerciseProgressDetail',
    component: () => import('@/views/classroom-v2/components/Subject/Exercise/ExerciseProgressDetail.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },

  // Flashcard Routes
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/flashcards/:chapterId',
    name: 'classroom.student.flashcardPractice',
    component: () => import('@/views/classroom-v2/components/Subject/FlashcardPractice.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },

  // Exam Routes
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/exam-progress/:examId',
    name: 'classroom.student.examProgressDetail',
    component: () => import('@/views/classroom-v2/components/Subject/Exam/ExamProgressDetail.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/exam/:examId/result',
    name: 'classroom.student.examResult',
    component: () => import('@/views/classroom-v2/components/Subject/components/screens/StudentExamResult.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },
  {
    path: '/classroom/student/:classroomId/subject/:subjectId/exercise/:exerciseId/result',
    name: 'classroom.student.exerciseResult',
    component: () => import('@/views/classroom-v2/components/Subject/components/screens/StudentExerciseResult.vue'),
    meta: {
      group: 'classroom-student-detail',
      title: 'classroom',
    },
  },

  // Exam Detail Routes (for taking exams)
  {
    path: '/classroom/student/:classroomId/exam/:examId',
    name: 'classroom.student.examDetail',
    component: () => import('@/views/classroom-v2/components/Detail/ExamDetail.vue'),
    meta: {
      group: 'classroomStudentExamDetail',
      icon: 'ri:edit-2-line',
      i18nKey: 'menu.classroomExamDetail',
      breadcrumb: {
        parent: ['home', 'classroom.student.detail'],
        title: 'Exam Detail',
        icon: 'ri:edit-2-line',
        i18nKey: 'menu.classroomExamDetail',
      },
    },
  },
  {
    path: '/classroom/student/:classroomId/exam-detail/:examId',
    name: 'classroom.student.examDetailScreen',
    component: () => import('@/views/classroom-v2/components/Detail/ExamDetailScreen.vue'),
    meta: {
      group: 'classroomStudentExamDetail',
      icon: 'ri:file-text-line',
      i18nKey: 'menu.classroomExamDetail',
      breadcrumb: {
        parent: ['home', 'classroom.student.detail'],
        title: 'Chi tiết bài kiểm tra',
        icon: 'ri:file-text-line',
        i18nKey: 'menu.classroomExamDetail',
      },
    },
  },
  {
    path: '/classroom/student/:classroomId/exam-waiting/:examId',
    name: 'classroom.student.examWaitingScreen',
    component: () => import('@/views/classroom-v2/components/Detail/ExamWaitingScreen.vue'),
    meta: {
      group: 'classroomStudentExamWaiting',
      icon: 'ri:time-line',
      i18nKey: 'menu.classroomExamWaiting',
      breadcrumb: {
        parent: ['home', 'classroom.student.detail'],
        title: 'Chờ bài kiểm tra',
        icon: 'ri:time-line',
        i18nKey: 'menu.classroomExamWaiting',
      },
    },
  },
  {
    path: '/classroom/student/:classroomId/exam-ended/:examId',
    name: 'classroom.student.examEndedScreen',
    component: () => import('@/views/classroom-v2/components/Detail/ExamEndedScreen.vue'),
    meta: {
      group: 'classroomStudentExamEnded',
      icon: 'ri:time-line',
      i18nKey: 'menu.classroomExamEnded',
      breadcrumb: {
        parent: ['home', 'classroom.student.detail'],
        title: 'Bài kiểm tra đã kết thúc',
        icon: 'ri:time-line',
        i18nKey: 'menu.classroomExamEnded',
      },
    },
  },
  {
    path: '/classroom/student/:classroomId/exam-completion',
    name: 'classroom.student.examCompletion',
    component: () => import('@/views/classroom-v2/components/Detail/CompletionScreen.vue'),
    meta: {
      group: 'classroomStudentExamCompletion',
      icon: 'ri:trophy-line',
      i18nKey: 'menu.classroomExamCompletion',
      breadcrumb: {
        parent: ['home', 'classroom.student.detail'],
        title: 'Bài kiểm tra đã được nộp',
        icon: 'ri:trophy-line',
        i18nKey: 'menu.classroomExamCompletion',
      },
    },
  },
  {
    path: '/classroom/student/:classroomId/exam-tab',
    name: 'classroom.student.examTab',
    component: () => import('@/views/classroom-v2/components/Detail/ExamTab.vue'),
    meta: {
      group: 'classroomStudentExamTab',
      icon: 'ri:file-edit-line',
      i18nKey: 'menu.classroomExam',
      breadcrumb: {
        parent: ['home', 'classroom.student.detail'],
        title: 'Bài kiểm tra',
        icon: 'ri:file-edit-line',
        i18nKey: 'menu.classroomExam',
      },
    },
  },
  {
    path: '/classroom/student/:classroomId/elearning',
    name: 'classroom.student.elearning',
    component: () => import('@/views/elearning/index.vue'),
    meta: {
      group: 'classroom-student-elearning',
      icon: 'ri:graduation-cap-line',
      i18nKey: 'menu.aiElearning',
      breadcrumb: {
        parent: ['classroom.student.detail'],
        title: 'AI E-Learning',
        icon: 'ri:graduation-cap-line',
        i18nKey: 'menu.aiElearning',
        params: ['classroomId'],
      },
    },
  },
  {
    path: 'classroom/student/plan-storage',
    name: 'classroom.student.plan-storage',
    component: () => import('@/views/settings/PackageAndStorage/index.vue'),
    meta: {
      title: 'Package Storage',
    },
  },
  {
    path: 'classroom/student/knowledge/:knowledgeId/:documentId',
    name: 'classroom.student.knowledge.detail',
    component: () => import('@/views/settings/PackageAndStorage/Knowledge/DetailLayout.vue'),
    meta: {
      title: 'Package Storage',
    },
  },
  {
    path: 'classroom/student/knowledge/:knowledgeId/:documentId/settings',
    name: 'classroom.student.knowledge.setting',
    component: () => import('@/views/settings/PackageAndStorage/Knowledge/Settings.vue'),
    meta: {
      title: 'Package Storage',
    },
  },
]
// Helper functions to work with route configs
export function getRouteConfig(routeName: string): RouteConfig | undefined {
  // Handle workspace -> dashboard mapping for sider compatibility
  if (routeName === 'workspace')
    return getRouteConfig('dashboard')

  return ROUTE_CONFIGS.find(config => config.name === routeName)
}

export function getRouteIcon(routeName: string): string {
  const config = getRouteConfig(routeName)
  return config?.meta.icon || config?.meta.breadcrumb?.icon || 'ri:file-line'
}

export function getRouteI18nKey(routeName: string): string {
  const config = getRouteConfig(routeName)
  return config?.meta.i18nKey || config?.meta.breadcrumb?.i18nKey || 'menu.unknown'
}

export function getAllRouteConfigs(): RouteConfig[] {
  return ROUTE_CONFIGS
}

// Convert route config to Vue Router format
export function convertToVueRoutes(): RouteRecordRaw[] {
  return ROUTE_CONFIGS.map(config => ({
    path: config.path,
    name: config.name,
    component: config.component,
    meta: {
      ...config.meta,
    } as CustomRouteMeta,
  }))
}
